import * as React from "react";
import { useMemo } from "react";
import {
  FieldValues,
  Path,
  UseControllerProps,
  useWatch
} from "react-hook-form";

import { createFilterOptions } from "@mui/material/Autocomplete";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentClinic,
  selectCurrentRegionClinicsIds,
  selectHasRegions
} from "features/auth/auth-slice";
import { useGetStaffQuery } from "services/staff";
import { MinimalStaffMember, StaffFilters } from "services/types/staff.type";

const EXCLUDE_STAFF_IDS_DEFAULT_VALUE: number[] = [];
const STAFF_MEMBER_FORM_FIELD_DEFAULT_STYLES = { ml: 2, mt: 1 };

type StaffMemberFormFieldProps = {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  limitToCurrentClinic?: boolean;
  excludeStaffIds?: number[];
  sx?: SxProps;
};

export type RhfStaffMemberFormFieldProps<T extends FieldValues> =
  StaffMemberFormFieldProps & UseControllerProps<T>;

export default function StaffMemberFormField<T extends FieldValues>({
  control,
  name,
  label = "Staff",
  limitToCurrentClinic = false,
  required = false,
  disabled = false,
  excludeStaffIds = EXCLUDE_STAFF_IDS_DEFAULT_VALUE,
  sx = STAFF_MEMBER_FORM_FIELD_DEFAULT_STYLES
}: RhfStaffMemberFormFieldProps<T>) {
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);

  const clinic = useWatch<T>({ control, name: "clinic" as Path<T> });
  const selectedValue = useWatch<T>({ control, name });

  const clinicIds = useMemo(() => {
    const limitedToClinic = clinic && clinic.id ? clinic : currentClinic;
    const clinicFilters =
      limitToCurrentClinic && limitedToClinic?.id ? [limitedToClinic.id] : [];
    if (hasRegions && !limitToCurrentClinic) {
      return currentRegionClinicsIds;
    }
    return clinicFilters;
  }, [
    clinic,
    currentClinic,
    limitToCurrentClinic,
    hasRegions,
    currentRegionClinicsIds
  ]);

  const staffFilters: StaffFilters = {
    clinicIds
  };
  const {
    data: staff = [],
    isLoading,
    isSuccess
  } = useGetStaffQuery(staffFilters);

  const options = useMemo(() => {
    let filteredStaff = staff.filter(
      (staff) => !excludeStaffIds.includes(staff.id)
    );

    // Always ensure the selected value is in the options if it exists
    if (selectedValue && selectedValue.id) {
      const isSelectedInOptions = filteredStaff.some(
        (s) => s.id === selectedValue.id
      );
      if (!isSelectedInOptions) {
        // Make sure the selected value has the required properties
        const normalizedSelectedValue = {
          ...selectedValue,
          fullName: selectedValue.fullName || selectedValue.full_name || "",
          full_name: selectedValue.full_name || selectedValue.fullName || ""
        };
        filteredStaff = [normalizedSelectedValue, ...filteredStaff];
      }
    }

    return filteredStaff;
  }, [excludeStaffIds, staff, selectedValue]);

  const filterOptions = createFilterOptions({
    stringify: (option: MinimalStaffMember) =>
      option.fullName || option.full_name || ""
  });

  // Show the field even while loading if we have a selected value
  if (!isSuccess && !selectedValue) {
    return <></>;
  }
  return (
    <AutocompleteFormField
      key={`${name}-${selectedValue?.id || "no-value"}-${options.length}`}
      name={name}
      label={label}
      disabled={disabled}
      required={required}
      options={options as MinimalStaffMember[]}
      isLoading={isLoading}
      getOptionLabel={(option) => option?.fullName || ""}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      control={control}
      sx={sx}
    />
  );
}
